import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Bell, LogOut, LayoutDashboard, KeyRound } from "lucide-react";
import { brand } from "@/tokens/brandTokens";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useFeatures, visibleNav } from "@/context/FeaturesContext";

const dashPath = (rol) => (rol === "admin" || rol === "super_admin" ? "/admin" : rol === "empresa" ? "/empresa" : rol === "egresado" ? "/egresado" : "/elegir-rol");

export const Header = () => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const nav = visibleNav(brand.nav, useFeatures());

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Logo />
        <nav className="hidden xl:flex items-center gap-4 2xl:gap-5 whitespace-nowrap">
          {nav.map((n) =>
            n.href ? (
              <a key={n.label} href={n.href} className="text-sm font-medium text-[var(--brand-primary)] hover:text-[var(--brand-accent)] transition-colors" data-testid={`nav-${n.label}`}>{n.label}</a>
            ) : (
              <Link key={n.label} to={n.to} className="text-sm font-medium text-[var(--brand-primary)] hover:text-[var(--brand-accent)] transition-colors" data-testid={`nav-${n.label}`}>{n.label}</Link>
            )
          )}
        </nav>
        <div className="hidden xl:flex items-center gap-4 ml-4 shrink-0">
          {user ? (
            <>
              <button onClick={() => navigate(dashPath(user.rol))} className="btn-outline flex items-center gap-2" data-testid="header-dashboard-btn"><LayoutDashboard size={16}/> Panel</button>
              <button onClick={() => navigate("/cuenta")} aria-label="Cambiar contraseña" title="Cambiar contraseña" className="text-[var(--brand-primary)] hover:text-[var(--brand-accent)] transition-colors p-2" data-testid="header-cuenta-btn"><KeyRound size={17} aria-hidden="true"/></button>
              <button onClick={logout} aria-label="Cerrar sesión" className="btn-primary flex items-center gap-2 whitespace-nowrap" data-testid="header-logout-btn"><LogOut size={16} aria-hidden="true"/> Cerrar sesión</button>
            </>
          ) : (
            <>
              <Link to="/registro?tipo=egresado" className="text-sm font-heading font-bold text-[var(--brand-primary)] hover:text-[var(--brand-accent)] transition-colors whitespace-nowrap" data-testid="header-egresado-btn">Soy egresado/a</Link>
              <Link to="/empresas" className="text-sm font-heading font-bold text-[var(--brand-primary)] hover:text-[var(--brand-accent)] transition-colors whitespace-nowrap" data-testid="header-empresa-btn">Soy empresa</Link>
              <Link to="/ingresar" className="btn-primary" data-testid="header-login-btn">Ingresar</Link>
            </>
          )}
        </div>
        <button className="xl:hidden text-[var(--brand-primary)]" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="xl:hidden border-t border-slate-100 bg-white px-4 py-4 space-y-3">
          {nav.map((n) =>
            n.href ? (
              <a key={n.label} href={n.href} className="block text-sm font-medium text-[var(--brand-primary)]">{n.label}</a>
            ) : (
              <Link key={n.label} to={n.to} onClick={() => setOpen(false)} className="block text-sm font-medium text-[var(--brand-primary)]">{n.label}</Link>
            )
          )}
          <div className="pt-3 flex flex-col gap-2">
            {user ? (
              <>
                <button onClick={() => { setOpen(false); navigate(dashPath(user.rol)); }} className="btn-outline">Panel</button>
                <button onClick={() => { setOpen(false); navigate("/cuenta"); }} className="btn-outline">Cambiar contraseña</button>
                <button onClick={logout} aria-label="Cerrar sesión" className="btn-primary flex items-center justify-center gap-2" data-testid="mobile-logout-btn"><LogOut size={16} aria-hidden="true"/> Cerrar sesión</button>
              </>
            ) : (
              <>
                <Link to="/registro?tipo=egresado" onClick={() => setOpen(false)} className="btn-outline text-center" data-testid="mobile-egresado-btn">Soy egresado/a</Link>
                <Link to="/empresas" onClick={() => setOpen(false)} className="btn-outline text-center" data-testid="mobile-empresa-btn">Soy empresa</Link>
                <Link to="/ingresar" onClick={() => setOpen(false)} className="btn-primary text-center">Ingresar</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export const Footer = () => {
  const nav = visibleNav(brand.nav, useFeatures());
  return (
  <footer className="mt-20 bg-[var(--brand-primary)] text-white">
    <div className="max-w-7xl mx-auto px-6 py-12 grid md:grid-cols-3 gap-8">
      <div>
        <div className="mb-4">
          <Logo variant="blanco" className="h-8" />
        </div>
        <p className="text-sm text-white/70 leading-relaxed">Formación + evaluación de soft skills + empleo. Una comunidad viva donde circula el conocimiento.</p>
      </div>
      <div>
        <h4 className="font-heading font-bold mb-3 text-[var(--brand-accent-hover)]">Plataforma</h4>
        <ul className="space-y-2 text-sm text-white/70">
          {nav.map((n) => <li key={n.label}>{n.href ? <a href={n.href}>{n.label}</a> : <Link to={n.to}>{n.label}</Link>}</li>)}
        </ul>
      </div>
      <div>
        <h4 className="font-heading font-bold mb-3 text-[var(--brand-accent-hover)]">Córdoba Crea Futuro</h4>
        <p className="text-sm text-white/70">La bolsa de talentos que conecta egresados/as, emprendedores/as y empresas de nuestra comunidad.</p>
      </div>
    </div>
    <div className="border-t border-white/10 py-5 px-6 text-center text-xs text-white/50 space-y-2">
      <div className="flex flex-wrap justify-center gap-x-4 gap-y-1">
        <Link to="/terminos" className="hover:text-white transition-colors underline" data-testid="footer-terminos">Términos y Condiciones</Link>
        <Link to="/privacidad" className="hover:text-white transition-colors underline" data-testid="footer-privacidad">Política de Privacidad</Link>
      </div>
      <div>© 2026 {brand.siteName} Talento · <a href="https://capabilia.org" target="_blank" rel="noreferrer" className="hover:text-white transition-colors" data-testid="footer-capabilia">Una creación de Capabilia</a></div>
    </div>
  </footer>
  );
};
